import React from 'react';
import { QuestionCircleOutlined, ReloadOutlined, BugOutlined } from '@ant-design/icons';
import { Dropdown, Switch } from 'antd';
import type { MenuProps } from 'antd';
import { useOnboarding } from '../../utils/hooks/useOnboarding';
import { TourType } from '../../types/onboarding';
import { Button } from './Button';

const OnboardingMenu: React.FC = () => {
  const {
    isOnboardingEnabled,
    toggleOnboarding,
    startTour,
    resetAllTours,
    completedTours,
    getTourProgress
  } = useOnboarding();

  const tourItems = [
    { key: TourType.FIRST_LOGIN, label: 'Getting Started Tour', icon: '🚀' },
    { key: TourType.OVERVIEW, label: 'Dashboard Overview', icon: '📊' },
    { key: TourType.SIDEBAR, label: 'Navigation Guide', icon: '🧭' },
    { key: TourType.CONVERSATIONS, label: 'Conversations Tour', icon: '💬' },
    { key: TourType.AGENTS, label: 'Agents Management', icon: '👥' },
    { key: TourType.SETTINGS, label: 'Settings Guide', icon: '⚙️' },
    { key: TourType.REPORTS, label: 'Reports & Analytics', icon: '📈' },
    { key: TourType.RECORDS, label: 'Chat Records', icon: '📝' },
    { key: TourType.DOCUMENTS, label: 'Documents Tour', icon: '📄' },
    { key: TourType.CALENDAR, label: 'Callback Requests', icon: '📅' },
    { key: TourType.SCHEDULER, label: 'Report Scheduler', icon: '⏰' },
    { key: TourType.LOGS, label: 'Activity Logs', icon: '📋' },
    { key: TourType.TEAMS, label: 'Teams Management', icon: '👨‍👩‍👧‍👦' }
  ];

  const items: MenuProps['items'] = [
    {
      key: 'tours',
      label: 'Available Tours',
      type: 'group',
      children: tourItems.map((tour) => ({
        key: tour.key,
        label: (
          <div className='flex items-center justify-between'>
            <span>
              {tour.icon} {tour.label}
            </span>
            {completedTours.includes(tour.key) && (
              <span className='text-green-600 text-xs ml-2'>✓</span>
            )}
          </div>
        ),
        onClick: () => {
          if (isOnboardingEnabled) {
            startTour(tour.key);
          }
        },
        disabled: !isOnboardingEnabled
      }))
    },
    {
      type: 'divider'
    },
    {
      key: 'reset',
      label: 'Reset All Tours',
      icon: <ReloadOutlined />,
      danger: true,
      onClick: () => {
        if (window.confirm('Are you sure you want to reset all tours? You will see them again.')) {
          resetAllTours();
        }
      }
    }
  ];

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      placement='bottomRight'
      overlayStyle={{ minWidth: '280px' }}
      popupRender={(menu) => (
        <div className='onboarding-menu-popup'>
          <div className='onboarding-menu-header'>
            <div className='flex items-center justify-between'>
              <span>Enable Tours</span>
              <Switch
                size='small'
                checked={isOnboardingEnabled}
                onChange={toggleOnboarding}
                aria-label='Enable Tours'
              />
            </div>
            <div className='onboarding-menu-progress'>
              <div className='text-xs text-gray-500 mb-1'>Tour Progress</div>
              <div className='w-full bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-primary h-2 rounded-full transition-all duration-300'
                  style={{ width: `${getTourProgress()}%` }}
                />
              </div>
              <div className='text-xs text-gray-600 mt-1'>
                {completedTours.length} of {tourItems.length} completed
              </div>
            </div>
          </div>
          <div className='onboarding-menu-list'>{menu}</div>
          <div className='onboarding-menu-footer'>
            <Button
              size='md'
              className='w-full'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(
                  'https://sawport-team-d19g630i.atlassian.net/servicedesk/customer/portal/1/group/1/create/6',
                  '_blank'
                );
              }}>
              <span className='flex items-center justify-center gap-2'>
                <BugOutlined />
                <span>Report System Problem</span>
              </span>
            </Button>
          </div>
        </div>
      )}>
      <QuestionCircleOutlined
        style={{ fontSize: '18px', cursor: 'pointer' }}
        title='Help & Tours'
      />
    </Dropdown>
  );
};

export default OnboardingMenu;
